import { logger } from '@inboundx/shared/platform';
import { supabase } from '@/lib/supabase';
import { resolveWidgetApiKey } from '@/services/publishableApiKey';

/**
 * Self-serve onboarding client (IX-3768) — talks to the admin API's
 * /onboarding routes with the user's Supabase JWT.
 */

export type OnboardingErrorCode =
    | 'invalid_url'
    | 'free_email_not_allowed'
    | 'email_domain_mismatch'
    | 'domain_taken'
    | 'root_host_conflict'
    | 'claim_failed'
    | 'not_configured'
    | 'network_error';

export class OnboardingClaimError extends Error {
    readonly code: OnboardingErrorCode;
    readonly domain?: string;
    /** Short server-provided reason, safe to surface for troubleshooting. */
    readonly serverMessage?: string;

    constructor(code: OnboardingErrorCode, domain?: string, serverMessage?: string) {
        super(`Onboarding claim failed: ${code}`);
        this.name = 'OnboardingClaimError';
        this.code = code;
        this.domain = domain;
        this.serverMessage = serverMessage;
    }
}

/** Codes the server returns for user-fixable input — expected, not incidents. */
const EXPECTED_REJECTIONS: ReadonlySet<OnboardingErrorCode> = new Set([
    'invalid_url',
    'free_email_not_allowed',
    'email_domain_mismatch',
    'domain_taken',
    'root_host_conflict',
]);

export function claimErrorMessage(code: OnboardingErrorCode, domain?: string): string {
    switch (code) {
        case 'invalid_url':
            return 'That does not look like a valid website address. Try something like acme.com.';
        case 'free_email_not_allowed':
            return 'Personal email providers cannot claim a company website. Sign in with your work email (e.g. you@yourcompany.com).';
        case 'email_domain_mismatch':
            return `To claim ${domain ?? 'this website'}, sign in with an email address on that domain.`;
        case 'domain_taken':
            return `${domain ?? 'This website'} is already registered with Rose. If it belongs to your company, contact support@userose.ai.`;
        case 'root_host_conflict':
            return `Rose already crawls ${domain ?? 'this website'} from a different address. Contact support@userose.ai to point it at this one — retrying here will not change it.`;
        case 'not_configured':
            return 'Onboarding is not available in this environment.';
        case 'network_error':
            return 'Network error — check your connection and try again.';
        default:
            return 'Something went wrong while creating your agent. Please try again.';
    }
}

export interface OnboardingClaimPayload {
    website_url: string;
    company_name: string;
    language: string;
    /** Other languages the site serves, written to identity.additional_languages. */
    additional_languages?: string[];
    tone_preset: string;
    /** Hex colour detected from the site, applied as the widget's default. */
    brand_color?: string;
    /**
     * Staff-only test override (IX-3930): re-onboard an already-registered
     * self_serve domain instead of failing with `domain_taken`. The server
     * ignores it for non-staff, so it is safe to always send.
     */
    force?: boolean;
}

export interface OnboardingClaimResult {
    domain: string;
    client_id: string;
    operation_id?: string | null;
}

function getAdminApiBaseUrl(): string {
    const baseUrl = import.meta.env.VITE_ADMIN_API_BASE_URL?.trim();
    if (!baseUrl) {
        throw new OnboardingClaimError('not_configured');
    }
    return baseUrl.replace(/\/$/, '');
}

async function getAccessToken(): Promise<string> {
    const {
        data: { session },
        error,
    } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
        throw new OnboardingClaimError('claim_failed');
    }
    return session.access_token;
}

function parseErrorCode(detail: unknown): {
    code: OnboardingErrorCode;
    domain?: string;
    serverMessage?: string;
} {
    if (detail && typeof detail === 'object' && 'code' in detail) {
        const record = detail as { code?: unknown; domain?: unknown; message?: unknown };
        const code = typeof record.code === 'string' ? record.code : '';
        const domain = typeof record.domain === 'string' ? record.domain : undefined;
        const serverMessage = typeof record.message === 'string' ? record.message : undefined;
        const known: OnboardingErrorCode[] = [
            'invalid_url',
            'free_email_not_allowed',
            'email_domain_mismatch',
            'domain_taken',
            'root_host_conflict',
            'claim_failed',
        ];
        if ((known as string[]).includes(code)) {
            return { code: code as OnboardingErrorCode, domain, serverMessage };
        }
    }
    // Unknown shape (proxy error page, plain FastAPI detail string, …) — keep
    // whatever text we got so the user sees more than a generic failure.
    if (typeof detail === 'string' && detail) {
        return { code: 'claim_failed', serverMessage: detail.slice(0, 200) };
    }
    return { code: 'claim_failed' };
}

export async function claimDomain(payload: OnboardingClaimPayload): Promise<OnboardingClaimResult> {
    const baseUrl = getAdminApiBaseUrl();
    const accessToken = await getAccessToken();

    let response: Response;
    try {
        response = await fetch(`${baseUrl}/onboarding/claim`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
    } catch (error) {
        logger.error('Onboarding claim network error', error);
        throw new OnboardingClaimError('network_error');
    }

    if (!response.ok) {
        let detail: unknown = null;
        try {
            detail = (await response.json())?.detail;
        } catch {
            // Non-JSON error body — fall through to generic code.
        }
        const { code, domain, serverMessage } = parseErrorCode(detail);
        // Validation rejections are normal wizard outcomes (the user retypes the
        // URL, signs in with another mailbox, contacts support). Only genuine
        // failures go through logger.error, which reports to Sentry — otherwise
        // every mistyped domain shows up as a production error to triage.
        const log = EXPECTED_REJECTIONS.has(code) ? logger.info : logger.error;
        log('Onboarding claim rejected', { status: response.status, code, serverMessage });
        throw new OnboardingClaimError(
            code,
            domain,
            serverMessage ?? `HTTP ${response.status} from ${baseUrl}`
        );
    }

    return (await response.json()) as OnboardingClaimResult;
}

export interface OnboardingStatus {
    provisioned: boolean;
    domains: string[];
    /**
     * Whether an unprovisioned user may still self-serve onboard: a corporate
     * mailbox whose company (registrable domain, incl. subdomain rows) no client
     * owns yet. False for a colleague of an existing customer or a personal
     * mailbox the claim step would reject.
     */
    canOnboard: boolean;
}

/**
 * Ask the admin API whether the signed-in user is provisioned and whether they
 * may still self-serve onboard. Used to route an unprovisioned user to the
 * onboarding wizard (`canOnboard`) vs. the access screen. The caller fails open
 * to the wizard so a transient error never blocks a genuine first-time signup —
 * the claim step still rejects a duplicate with `domain_taken`.
 */
export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
    const baseUrl = getAdminApiBaseUrl();
    const accessToken = await getAccessToken();
    const response = await fetch(`${baseUrl}/onboarding/status`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
        throw new Error(`Onboarding status failed: HTTP ${response.status}`);
    }
    const data = (await response.json()) as {
        provisioned?: boolean;
        domains?: string[];
        can_onboard?: boolean;
    };
    return {
        provisioned: Boolean(data.provisioned),
        domains: data.domains ?? [],
        canOnboard: Boolean(data.can_onboard),
    };
}

export interface PlaygroundStreamEvent {
    type: string;
    content?: string;
    sessionId?: string;
}

export interface PlaygroundStreamHandlers {
    onToken: (content: string) => void;
    onComplete: (fullText: string) => void;
    /** code is machine-readable; detail carries any upstream text worth showing. */
    onError: (code: string, detail?: string) => void;
}

// The /rose prefix belongs to the Cloudflare proxy in front of the agent API.
// A local search API (`PORT=8081 just dev <env>`) has no prefix — set
// VITE_IX_API_URL=http://localhost:8081 and it works as-is.
const DEFAULT_ROSE_API_URL = 'https://api.userose.ai/rose';
const STREAM_PATH = '/api/lightrag/query/stream';
// Excluded from client-facing analytics, same as the staff pre-prod playground.
const PLAYGROUND_DEPLOYMENT_TARGET = 'preprod-ui';

/**
 * Stream one playground chat turn against the public agent API — the same
 * endpoint the website widget uses (`data: {json}\n` line protocol with
 * `token` / `complete` / `error` / `stream_error` events).
 *
 * The X-API-Key here is the widget key, which already ships in the client
 * page's `data-api-key` attribute — using it from the backoffice adds no
 * exposure beyond what the public widget endpoint has by design. A client
 * with its own key (IX-5066) refuses the shared one, so it is resolved per domain.
 */
export async function streamPlaygroundQuery(
    params: { domain: string; query: string; sessionId: string },
    handlers: PlaygroundStreamHandlers,
    signal?: AbortSignal
): Promise<void> {
    const baseUrl = (import.meta.env.VITE_IX_API_URL?.trim() || DEFAULT_ROSE_API_URL).replace(
        /\/$/,
        ''
    );
    const apiKey = await resolveWidgetApiKey(params.domain);
    if (!apiKey) {
        logger.error(
            'IX_API_KEY missing from frontend env — playground cannot reach the agent API'
        );
        handlers.onError('not_configured');
        return;
    }

    let response: Response;
    try {
        response = await fetch(`${baseUrl}${STREAM_PATH}`, {
            method: 'POST',
            headers: {
                'X-API-Key': apiKey,
                'Content-Type': 'application/json',
                Accept: 'text/plain',
            },
            body: JSON.stringify({
                query: params.query,
                siteName: params.domain,
                sessionId: params.sessionId,
                deploymentTarget: PLAYGROUND_DEPLOYMENT_TARGET,
            }),
            signal,
        });
    } catch (error) {
        if (signal?.aborted) return;
        logger.error('Playground stream network error', error);
        handlers.onError('network_error', `Could not reach ${baseUrl} (network or CORS)`);
        return;
    }

    if (!response.ok || !response.body) {
        let bodySnippet = '';
        try {
            bodySnippet = (await response.text()).slice(0, 200);
        } catch {
            // Body unreadable — status alone will have to do.
        }
        logger.error('Playground stream rejected', { status: response.status, bodySnippet });
        handlers.onError(`http_${response.status}`, bodySnippet);
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';

    const handleLine = (line: string): boolean => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) return false;
        let event: PlaygroundStreamEvent;
        try {
            event = JSON.parse(trimmed.slice(5).trim()) as PlaygroundStreamEvent;
        } catch {
            return false;
        }
        if (event.type === 'token' && typeof event.content === 'string') {
            fullText += event.content;
            handlers.onToken(event.content);
        } else if (event.type === 'complete') {
            handlers.onComplete(typeof event.content === 'string' ? event.content : fullText);
            return true;
        } else if (event.type === 'error' || event.type === 'stream_error') {
            handlers.onError('agent_error', event.content);
            return true;
        }
        return false;
    };

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
                if (handleLine(line)) return;
            }
        }
        // Stream ended without an explicit complete event.
        if (fullText) {
            handlers.onComplete(fullText);
        } else if (!signal?.aborted) {
            handlers.onError('empty_stream');
        }
    } catch (error) {
        if (signal?.aborted) return;
        logger.error('Playground stream read failed', error);
        handlers.onError('stream_interrupted');
    }
}
